import type {
  CreatedIntakeForm,
  CreateIntakeFormRequest,
  GenericFormText,
  IntakeFormListItem,
  IntakeFormStarterTemplate,
  UpdatedGenericForm,
  UpdateGenericFormTextRequest,
} from '@/definitions/intakeForm'
import api from '@/services/api'

// `base_form_id` is a free-form column rather than a UUID, so a value carrying a
// slash would otherwise build a request to a different path.
const genericFormPath = (orgId: string, baseFormId: string) =>
  `/api/v1/forms/${orgId}/generic/${encodeURIComponent(baseFormId)}`

// Bounded because the definition box cannot be typed in until this settles, and
// the shared client sets no timeout — a connection that hangs rather than fails
// would leave the operator on a spinner with no way to paste their own definition.
const STARTER_TEMPLATE_TIMEOUT_MS = 10_000

// Bounded for a harder version of the same reason: the edit box shows no fields
// at all until this settles, so a connection that hangs rather than fails leaves
// the operator on a spinner with nothing to do.
const GENERIC_FORM_TEXT_TIMEOUT_MS = 10_000

// Longer than the reads, but bounded: the modal can't be closed mid-write, so a
// hung request leaves only a reload, which discards everything typed.
const WRITE_TIMEOUT_MS = 30_000

/**
 * The definition the new-form box opens pre-filled with.
 *
 * Served rather than bundled here so it travels with the validator that has to
 * accept it. Not organization-specific — every organization starts from this one.
 */
export const FetchIntakeFormStarterTemplate =
  async (): Promise<IntakeFormStarterTemplate> => {
    const response = await api.get<IntakeFormStarterTemplate>(
      '/api/v1/forms/starter-template',
      { timeout: STARTER_TEMPLATE_TIMEOUT_MS }
    )
    return response.data
  }

/**
 * Every intake form the organization has, generic and custom alike.
 *
 * One request however many forms it has, and one entry per form rather than one
 * per revision — the response carries each form's current state.
 */
export const FetchIntakeForms = async (
  orgId: string
): Promise<IntakeFormListItem[]> => {
  const response = await api.get<IntakeFormListItem[]>(`/api/v1/forms/${orgId}`)
  return response.data
}

/**
 * Store a custom intake form. It is live at its patient link once this returns.
 *
 * Refuses a definition carrying warnings until `acknowledge_warnings` is set, so
 * a caller has to show them before anything is stored.
 */
export const CreateIntakeForm = async (
  orgId: string,
  data: CreateIntakeFormRequest
): Promise<CreatedIntakeForm> => {
  const response = await api.post<CreatedIntakeForm>(
    `/api/v1/forms/${orgId}`,
    data,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}

/**
 * The text a generic intake form currently serves, for an edit to start from.
 *
 * Read separately from the forms list rather than carried on each row: the list
 * does not load form definitions, and this reads into one.
 */
export const FetchGenericFormText = async (
  orgId: string,
  baseFormId: string
): Promise<GenericFormText> => {
  const response = await api.get<GenericFormText>(genericFormPath(orgId, baseFormId), {
    timeout: GENERIC_FORM_TEXT_TIMEOUT_MS,
  })
  return response.data
}

/**
 * Change a generic intake form's title, intro text and completion text.
 *
 * The change is stored as a new revision of the same form, so the patient link
 * already given out keeps working and serves the new text. Returns the form's
 * updated state, whose `display_name` is the new title.
 *
 * Sending the current values writes nothing, which is what makes a
 * sequential retry safe. `form_version` is the revision the submitted text was
 * read off: a save built off one since superseded is refused rather than
 * reverting the fields the operator never touched.
 */
export const UpdateGenericFormText = async (
  orgId: string,
  baseFormId: string,
  data: UpdateGenericFormTextRequest
): Promise<UpdatedGenericForm> => {
  const response = await api.put<UpdatedGenericForm>(
    genericFormPath(orgId, baseFormId),
    data,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}
